/**
 * Lazy-loads Quick Add JS on first interaction with a tile trigger.
 * CSS stays eager so the tile icon is styled on first paint.
 * Injects data provider + custom element scripts once, then re-dispatches
 * the activating click so the modal opens without a second user action.
 */
(function () {
  'use strict';

  const TRIGGER_SELECTOR = '.quick-add-tile__trigger';
  const TILE_SELECTOR = 'quick-add-tile';

  const scriptEl = document.currentScript;
  const assets = {
    dataJs: scriptEl?.dataset.dataJs || '',
    tileJs: scriptEl?.dataset.tileJs || '',
  };

  let loadPromise = null;
  let isReady = false;

  /**
   * @param {EventTarget|null} target
   * @returns {HTMLButtonElement|null}
   */
  function findTrigger(target) {
    if (!(target instanceof Element)) return null;

    if (target.matches(TRIGGER_SELECTOR)) {
      return /** @type {HTMLButtonElement} */ (target);
    }

    const trigger = target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      return /** @type {HTMLButtonElement} */ (trigger);
    }

    const tile = target.closest(TILE_SELECTOR);
    if (tile) {
      return tile.querySelector(TRIGGER_SELECTOR);
    }

    return null;
  }

  /**
   * @param {string} src
   * @returns {Promise<void>}
   */
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (!src || document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`[quick-add-tile-loader] failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * @returns {Promise<void>}
   */
  function loadQuickAddAssets() {
    if (isReady) {
      return Promise.resolve();
    }

    if (!loadPromise) {
      loadPromise = (async () => {
        await loadScript(assets.dataJs);
        await loadScript(assets.tileJs);
        await customElements.whenDefined('quick-add-tile');
        isReady = true;
      })().catch((error) => {
        loadPromise = null;
        throw error;
      });
    }

    return loadPromise;
  }

  /**
   * @param {Event} event
   */
  function handleClick(event) {
    const trigger = findTrigger(event.target);
    if (!trigger || isReady) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    loadQuickAddAssets()
      .then(() => {
        trigger.click();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * @param {FocusEvent} event
   */
  function handleFocusIn(event) {
    if (!findTrigger(event.target)) return;
    loadQuickAddAssets().catch((error) => {
      console.error(error);
    });
  }

  document.addEventListener('click', handleClick, true);
  document.addEventListener('focusin', handleFocusIn, true);
})();
